"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Dropdown, Button, Checkbox, Divider, Input, Spin } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { useStore } from "@/store/useStore";
import { useClient, type Client } from "@/context/ClientContext";
import { clientDebugLog } from "@/lib/debug/clientDebug";

type CheckboxValueType = string | number;

interface CheckboxDropdownProps {
  value?: CheckboxValueType[];
  onChange?: (values: CheckboxValueType[]) => void;
  onClientNamesChange?: (clientNames: Record<string, string>) => void; // New prop for client names
  // New props for selection modes
  singleSelect?: boolean;
  useZustandMulti?: boolean; // Use Zustand store for multi-select
  placeholder?: string;
  customButtonClass?: string; // Allow custom button styling
  userClientIds?: string[]; // Filter clients to only show user's assigned clients
  isAdmin?: boolean; // Whether the user is an admin
  contextId?: string; // Unique identifier for this dropdown context (e.g., 'dashboard', 'userForm')
}

const CheckboxDropdown: React.FC<CheckboxDropdownProps> = ({
  value = [],
  onChange,
  onClientNamesChange,
  singleSelect = false,
  useZustandMulti = false,
  placeholder = "Select Client",
  customButtonClass,
  userClientIds,
  isAdmin = false,
  contextId = 'default',
}) => {
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  const [persistedTotalCount, setPersistedTotalCount] = useState<number>(0);
  const hasHydrated = useRef(false);
  
  // Local state for context-specific selections (when not using global Zustand)
  const [localSelectedClientIds, setLocalSelectedClientIds] = useState<string[]>([]);
  const [localSelectedClientNames, setLocalSelectedClientNames] = useState<Record<string, string>>({});
  const {
    selectedClientId,
    setSelectedClientId,
    selectedClientIds,
    setSelectedClientIds,
    selectedClientNames,
    setSelectedClientNames,
    hydrateClients,
  } = useStore();

  const { 
    clientsQuery, 
    allClients, 
    fetchAllClients, 
    totalClientsCount, 
    allClientsForSelectAll, 
    isLoadingAllClients
  } = useClient();

  // Local search state to avoid interfering with other components
  const [localSearchQuery, setLocalSearchQuery] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [activeClientsFilter, setActiveClientsFilter] = useState<boolean>(false);
  const [inactiveClientsFilter, setInactiveClientsFilter] = useState<boolean>(false);

  const {
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = clientsQuery;

  useEffect(() => {
    clientDebugLog("dropdown_mount", {
      contextId,
      isAdmin,
      singleSelect,
      useZustandMulti,
      userClientIdsCount: userClientIds?.length || 0,
    });
  }, [contextId, isAdmin, singleSelect, useZustandMulti, userClientIds]);

  // Determine current values based on mode and context
  const currentValues = useMemo(() => {
    if (singleSelect) {
      return selectedClientId ? [selectedClientId] : [];
    }
    
    // For UserFormWizard context, use local state to avoid interfering with DashboardLayout
    if (useZustandMulti && contextId === 'userForm') {
      return localSelectedClientIds;
    }
    
    return useZustandMulti ? selectedClientIds : value;
  }, [singleSelect, selectedClientId, useZustandMulti, selectedClientIds, value, contextId, localSelectedClientIds]);

  // Create a hashmap for efficient selected state tracking
  const selectedItemsMap = useMemo(() => {
    const map = new Map<string, boolean>();
    currentValues.forEach(id => map.set(id.toString(), true));
    return map;
  }, [currentValues]);

  // Deduplicate clients by account_uid to handle API duplicates
  const deduplicatedClients = useMemo(() => {
    const seen = new Set<string>();
    return allClients.filter(client => {
      if (!client.account_uid) return false;
      if (seen.has(client.account_uid)) {
        return false; // Skip duplicates
      }
      seen.add(client.account_uid);
      return true;
    });
  }, [allClients]);

  // Deduplicate allClientsForSelectAll as well
  const deduplicatedAllClientsForSelectAll = useMemo(() => {
    const seen = new Set<string>();
    return allClientsForSelectAll.filter(client => {
      if (!client.account_uid) return false;
      if (seen.has(client.account_uid)) {
        return false; // Skip duplicates
      }
      seen.add(client.account_uid);
      return true;
    });
  }, [allClientsForSelectAll]);

  // Infinite-query pages can be empty while fetchAllClients still filled allClientsForSelectAll
  // (e.g. response shape mismatch). Category rows use the latter; list rows must use the same source.
  const clientsForDisplay = useMemo(() => {
    if (deduplicatedClients.length > 0) {
      return deduplicatedClients;
    }
    return deduplicatedAllClientsForSelectAll;
  }, [deduplicatedClients, deduplicatedAllClientsForSelectAll]);

  // Get the available clients for the user (filtered by permissions and deduplicated)
  const availableClients = useMemo(() => {
    if (userClientIds && userClientIds.length > 0) {
      const filtered = deduplicatedAllClientsForSelectAll.filter(
        (client) => client.account_uid && userClientIds.includes(client.account_uid)
      );
      return filtered;
    }
    return deduplicatedAllClientsForSelectAll;
  }, [deduplicatedAllClientsForSelectAll, userClientIds]);

  // Get the total count of available clients for the user (deduplicated)
  // Use persisted count if API count is 0 (during loading) or use API count
  // This should ALWAYS show the total count, not affected by Active/Inactive filters
  const availableClientsCount = useMemo(() => {
    if (!isAdmin) {
      return userClientIds ? userClientIds.length : 0;
    } else {
      // If we have a valid totalClientsCount from API, use it
      if (totalClientsCount > 0) {
        return totalClientsCount;
      } else if (persistedTotalCount > 0) {
        // If API count is 0 but we have persisted count, use persisted count
        return persistedTotalCount;
      } else {
        // Fallback to deduplicated count
        return deduplicatedAllClientsForSelectAll.length;
      }
    }
  }, [isAdmin, userClientIds, totalClientsCount, persistedTotalCount, deduplicatedAllClientsForSelectAll]);



  // Get currently visible clients (filtered by search, permissions, and status, deduplicated)
  const visibleClients = useMemo(() => {
    let filteredClients = userClientIds
      ? clientsForDisplay.filter(
          (client) =>
            client.account_uid && userClientIds.includes(client.account_uid)
        )
      : clientsForDisplay;

    // Apply search filter if there's a search query
    if (localSearchQuery && localSearchQuery.trim()) {
      filteredClients = filteredClients.filter(client =>
        client.account_name?.toLowerCase().includes(localSearchQuery.toLowerCase())
      );
    }

    // Sort alphabetically by account_name
    return filteredClients.sort((a, b) => {
      const nameA = a.account_name || '';
      const nameB = b.account_name || '';
      return nameA.localeCompare(nameB, undefined, { 
        numeric: true, 
        sensitivity: 'base' 
      });
    });
  }, [clientsForDisplay, userClientIds, localSearchQuery]);

  useEffect(() => {
    clientDebugLog("dropdown_state_snapshot", {
      contextId,
      isDropdownOpen,
      isLoading,
      isError,
      allClientsCount: allClients.length,
      allClientsForSelectAllCount: allClientsForSelectAll.length,
      clientsForDisplayCount: clientsForDisplay.length,
      visibleClientsCount: visibleClients.length,
      selectedCount: currentValues.length,
      totalClientsCount,
      activeFilter: activeClientsFilter,
      inactiveFilter: inactiveClientsFilter,
      searchQuery: localSearchQuery,
    });
  }, [
    contextId,
    isDropdownOpen,
    isLoading,
    isError,
    allClients.length,
    allClientsForSelectAll.length,
    clientsForDisplay.length,
    visibleClients.length,
    currentValues.length,
    totalClientsCount,
    activeClientsFilter,
    inactiveClientsFilter,
    localSearchQuery,
  ]);

  // Create separate arrays for selected and unselected clients for better UX
  const { selectedOptions, unselectedOptions } = useMemo(() => {
    const selected: Client[] = [];
    const unselected: Client[] = [];

    // Categorize currently loaded options
    visibleClients.forEach((client) => {
      if (client.account_uid && selectedItemsMap.has(client.account_uid)) {
        selected.push(client);
      } else {
        unselected.push(client);
      }
    });

    // Sort both arrays alphabetically by account_name
    const sortClients = (a: Client, b: Client) => {
      const nameA = a.account_name || '';
      const nameB = b.account_name || '';
      return nameA.localeCompare(nameB, undefined, { 
        numeric: true, 
        sensitivity: 'base' 
      });
    };

    return { 
      selectedOptions: selected.sort(sortClients), 
      unselectedOptions: unselected.sort(sortClients) 
    };
  }, [visibleClients, selectedItemsMap, currentValues]);

  // Check if all visible clients are selected
  const isAllVisibleSelected = useMemo(() => {
    if (visibleClients.length === 0) return false;
    return visibleClients.every(client => 
      client.account_uid && selectedItemsMap.has(client.account_uid)
    );
  }, [visibleClients, selectedItemsMap]);

  // Check if some (but not all) visible clients are selected
  const isIndeterminate = useMemo(() => {
    if (visibleClients.length === 0) return false;
    const selectedCount = visibleClients.filter(client => 
      client.account_uid && selectedItemsMap.has(client.account_uid)
    ).length;
    return selectedCount > 0 && selectedCount < visibleClients.length;
  }, [visibleClients, selectedItemsMap]);

  // Auto-update filter states based on actual selection
  useEffect(() => {
    const activeClients = deduplicatedAllClientsForSelectAll.filter(c => c.account_status === 'Active');
    const inactiveClients = deduplicatedAllClientsForSelectAll.filter(c => c.account_status === 'Inactive');
    
    const selectedActiveCount = activeClients.filter(c => 
      c.account_uid && currentValues.includes(c.account_uid)
    ).length;
    
    const selectedInactiveCount = inactiveClients.filter(c => 
      c.account_uid && currentValues.includes(c.account_uid)
    ).length;
    
    // Update Active filter state - only if all are selected or none are selected
    if (selectedActiveCount === activeClients.length && activeClients.length > 0) {
      setActiveClientsFilter(true);
    } else if (selectedActiveCount === 0) {
      setActiveClientsFilter(false);
    }
    // Don't change state if partially selected
    
    // Update Inactive filter state - only if all are selected or none are selected
    if (selectedInactiveCount === inactiveClients.length && inactiveClients.length > 0) {
      setInactiveClientsFilter(true);
    } else if (selectedInactiveCount === 0) {
      setInactiveClientsFilter(false);
    }
    // Don't change state if partially selected
  }, [currentValues, deduplicatedAllClientsForSelectAll]);

  // Update account names when allClients changes (use deduplicated clients)
  useEffect(() => {
    const nameMap = clientsForDisplay.reduce((acc, client) => {
      if (client.account_uid && client.account_name) {
        acc[client.account_uid] = client.account_name;
      }
      return acc;
    }, {} as Record<string, string>);
    setAccountNames(nameMap);
    
    // Update client names for existing selections when new data is available
    if (useZustandMulti) {
      if (contextId === 'userForm' && localSelectedClientIds.length > 0) {
        // Update local client names for userForm context
        const updatedNames = { ...localSelectedClientNames };
        let hasUpdates = false;
        
        localSelectedClientIds.forEach(clientId => {
          const newName = nameMap[clientId];
          if (newName && (!updatedNames[clientId] || updatedNames[clientId] === `Client ${clientId}`)) {
            updatedNames[clientId] = newName;
            hasUpdates = true;
          }
        });
        
        if (hasUpdates) {
          setLocalSelectedClientNames(updatedNames);
          if (onClientNamesChange) {
            onClientNamesChange(updatedNames);
          }
        }
      } else if (contextId === 'dashboard' && selectedClientIds.length > 0) {
        // Update global client names for dashboard context
        const updatedNames = { ...selectedClientNames };
        let hasUpdates = false;
        
        selectedClientIds.forEach(clientId => {
          const newName = nameMap[clientId];
          if (newName && (!updatedNames[clientId] || updatedNames[clientId] === `Client ${clientId}`)) {
            updatedNames[clientId] = newName;
            hasUpdates = true;
          }
        });
        
        if (hasUpdates) {
          setSelectedClientNames(updatedNames);
        }
      }
    }
  }, [clientsForDisplay, contextId, useZustandMulti, localSelectedClientIds, localSelectedClientNames, selectedClientIds, selectedClientNames, setSelectedClientNames, onClientNamesChange]);

  // Hydrate client selection from localStorage on component mount (only once)
  useEffect(() => {
    if (useZustandMulti && !hasHydrated.current) {
      // Call hydrateClients to restore state from localStorage
      hydrateClients();
      hasHydrated.current = true;
    }
  }, [useZustandMulti, hydrateClients]);

  // Initialize local state with value prop for userForm context
  useEffect(() => {
    if (useZustandMulti && contextId === 'userForm' && value && value.length > 0) {
      setLocalSelectedClientIds(value as string[]);
      
      // Also initialize client names if available
      if (allClients.length > 0) {
        const clientNamesMap: Record<string, string> = {};
        (value as string[]).forEach((clientId) => {
          const client = allClients.find((c) => c.account_uid === clientId);
          clientNamesMap[clientId] = client?.account_name || `Client ${clientId}`;
        });
        setLocalSelectedClientNames(clientNamesMap);
      }
    }
  }, [useZustandMulti, contextId, value, allClients]);

  // Update local state when value prop changes (for userForm context)
  useEffect(() => {
    if (useZustandMulti && contextId === 'userForm') {
      const newValue = value || [];
      setLocalSelectedClientIds(newValue as string[]);
      
      // Update client names when allClients data becomes available
      if (allClients.length > 0) {
        const clientNamesMap: Record<string, string> = {};
        (newValue as string[]).forEach((clientId) => {
          const client = allClients.find((c) => c.account_uid === clientId);
          clientNamesMap[clientId] = client?.account_name || `Client ${clientId}`;
        });
        setLocalSelectedClientNames(clientNamesMap);
      }
    }
  }, [useZustandMulti, contextId, value, allClients]);

  // Persist total count when it becomes available from API
  useEffect(() => {
    if (totalClientsCount > 0) {
      setPersistedTotalCount(totalClientsCount);
      // Store in localStorage for persistence across refreshes
      if (typeof window !== 'undefined') {
        localStorage.setItem('persistedTotalCount', totalClientsCount.toString());
      }
    }
  }, [totalClientsCount]);

  // Load persisted total count on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('persistedTotalCount');
      if (stored) {
        const count = parseInt(stored, 10);
        if (count > 0) {
          setPersistedTotalCount(count);
        }
      }
    }
  }, []);

  // Clear local search query when component unmounts or context changes
  useEffect(() => {
    return () => {
      setLocalSearchQuery('');
    };
  }, [contextId]);

  const handleSelectAll = useCallback(async (checked: boolean) => {
    if (singleSelect) return;

    if (checked) {
      // Select ALL available clients (not just visible ones, deduplicated)
      let clientsToSelect = availableClients;
      
      if (isAdmin) {
        // For admin users, always fetch all clients when selecting all
        const fetchedClients = await fetchAllClients(true);
        
        // Deduplicate fetched clients
        const seen = new Set<string>();
        clientsToSelect = fetchedClients.filter(client => {
          if (!client.account_uid) return false;
          if (seen.has(client.account_uid)) {
            return false; // Skip duplicates
          }
          seen.add(client.account_uid);
          return true;
        });
        
      } else {
        // For non-admin users, use their available clients
        clientsToSelect = availableClients;
      }

      // Apply status filtering to selection
      if (activeClientsFilter && !inactiveClientsFilter) {
        clientsToSelect = clientsToSelect.filter(client =>
          client.account_status === 'Active'
        );
      } else if (inactiveClientsFilter && !activeClientsFilter) {
        clientsToSelect = clientsToSelect.filter(client =>
          client.account_status === 'Inactive'
        );
      }
      // If both or neither are selected, use all clients

      const accountIds = clientsToSelect
        .map((client) => client.account_uid)
        .filter(Boolean) as string[];

      if (useZustandMulti) {
        // Handle context-specific state management
        if (contextId === 'userForm') {
          // Use local state for UserFormWizard to avoid interfering with DashboardLayout
          setLocalSelectedClientIds(accountIds);
          
                     // Store client names for all selected clients in local state
           const clientNamesMap = clientsToSelect.reduce((acc, client) => {
             if (client.account_uid) {
               // Try to find the best available name
               let clientName = client.account_name;
               if (!clientName) {
                 // Try to find in accountNames cache
                 clientName = accountNames[client.account_uid] || '';
               }
               if (clientName) {
                 acc[client.account_uid] = clientName;
               }
             }
             return acc;
           }, {} as Record<string, string>);
          setLocalSelectedClientNames(clientNamesMap);
          
          // Notify parent component of changes
          if (onChange) {
            onChange(accountIds as CheckboxValueType[]);
          }
          if (onClientNamesChange) {
            onClientNamesChange(clientNamesMap);
          }
        } else {
          // Use global Zustand state for DashboardLayout and other contexts
          setSelectedClientIds(accountIds);
          
                     // Store client names for all selected clients
           const clientNamesMap = clientsToSelect.reduce((acc, client) => {
             if (client.account_uid) {
               // Try to find the best available name
               let clientName = client.account_name;
               if (!clientName) {
                 // Try to find in accountNames cache
                 clientName = accountNames[client.account_uid] || '';
               }
               if (clientName) {
                 acc[client.account_uid] = clientName;
               }
             }
             return acc;
           }, {} as Record<string, string>);
          setSelectedClientNames(clientNamesMap);
        }
        
      } else if (onChange) {
        // For non-Zustand mode, replace with all available clients
        onChange(accountIds as CheckboxValueType[]);
        
        // Notify parent of client names
        if (onClientNamesChange) {
          const clientNamesMap = clientsToSelect.reduce((acc, client) => {
            if (client.account_uid) {
              // Try to find the best available name
              let clientName = client.account_name;
              if (!clientName) {
                // Try to find in accountNames cache
                clientName = accountNames[client.account_uid] || '';
              }
              if (clientName) {
                acc[client.account_uid] = clientName;
              }
            }
            return acc;
          }, {} as Record<string, string>);
          onClientNamesChange(clientNamesMap);
        }
      }
    } else {
      // Deselect ALL selected clients (consistent behavior)
      if (useZustandMulti) {
        if (contextId === 'userForm') {
          // Clear local state for UserFormWizard
          setLocalSelectedClientIds([]);
          setLocalSelectedClientNames({});
          
          // Notify parent component of changes
          if (onChange) {
            onChange([]);
          }
          if (onClientNamesChange) {
            onClientNamesChange({});
          }
        } else {
          // Clear global Zustand state for DashboardLayout
          setSelectedClientIds([]);
          setSelectedClientNames({});
        }
      } else if (onChange) {
        // For non-Zustand mode, clear all selections
        onChange([]);
        
        // Notify parent of empty client names
        if (onClientNamesChange) {
          onClientNamesChange({});
        }
      }
    }
  }, [
    singleSelect,
    onChange,
    onClientNamesChange,
    availableClients,
    isAdmin,
    fetchAllClients,
    useZustandMulti,
    setSelectedClientIds,
    setSelectedClientNames,
    accountNames,
    activeClientsFilter,
    inactiveClientsFilter,
    contextId,
    localSelectedClientIds,
    selectedClientIds,
    localSelectedClientNames,
    selectedClientNames,
    setLocalSelectedClientIds,
    setLocalSelectedClientNames,
  ]);

  const handleSingleSelect = (accountId: string) => {
    if (singleSelect) {
      setSelectedClientId(accountId);
    } else {
      const isSelected = selectedItemsMap.has(accountId);
      
      // Try to find the client name from multiple sources
      let clientName = '';
      const selectedAccount = clientsForDisplay.find(
        (acc) => acc.account_uid === accountId
      );
      
      if (selectedAccount?.account_name) {
        clientName = selectedAccount.account_name;
      } else {
        // Try to find in allClientsForSelectAll (which might have more data)
        const allClientsAccount = allClientsForSelectAll.find(
          (acc) => acc.account_uid === accountId
        );
        if (allClientsAccount?.account_name) {
          clientName = allClientsAccount.account_name;
        } else {
          // Try to find in accountNames (cached names)
          clientName = accountNames[accountId] || '';
        }
      }

      if (useZustandMulti) {
        // Handle context-specific state management
        if (contextId === 'userForm') {
          // Use local state for UserFormWizard to avoid interfering with DashboardLayout
          if (isSelected) {
            // Remove from selection - ensure no duplicates
            const newSelection = localSelectedClientIds.filter((id) => id !== accountId);
            setLocalSelectedClientIds(newSelection);
            
            // Remove client name from local state
            const updatedNames = { ...localSelectedClientNames };
            delete updatedNames[accountId];
            setLocalSelectedClientNames(updatedNames);
            
            // Notify parent component of changes
            if (onChange) {
              onChange(newSelection as CheckboxValueType[]);
            }
            if (onClientNamesChange) {
              onClientNamesChange(updatedNames);
            }
          } else {
            // Add to selection - ensure no duplicates
            if (!localSelectedClientIds.includes(accountId)) {
              const newSelection = [...localSelectedClientIds, accountId];
              setLocalSelectedClientIds(newSelection);
              
              // Add client name to local state
              const newNames = { ...localSelectedClientNames };
              if (clientName) {
                newNames[accountId] = clientName;
              }
              setLocalSelectedClientNames(newNames);
              
              // Notify parent component of changes
              if (onChange) {
                onChange(newSelection as CheckboxValueType[]);
              }
              if (onClientNamesChange) {
                onClientNamesChange(newNames);
              }
            }
          }
        } else {
          // Use global Zustand state for DashboardLayout and other contexts
          if (isSelected) {
            // Remove from selection - ensure no duplicates
            const newSelection = selectedClientIds.filter((id) => id !== accountId);
            setSelectedClientIds(newSelection);
            
            // Remove client name from store
            const updatedNames = { ...selectedClientNames };
            delete updatedNames[accountId];
            setSelectedClientNames(updatedNames);
          } else {
            // Add to selection - ensure no duplicates
            if (!selectedClientIds.includes(accountId)) {
              const newSelection = [...selectedClientIds, accountId];
              setSelectedClientIds(newSelection);
              
              // Add client name to store
              if (clientName) {
                setSelectedClientNames({
                  ...selectedClientNames,
                  [accountId]: clientName,
                });
              }
            }
          }
        }
      } else if (onChange) {
        const currentVals = currentValues || [];
        
        // Build client names map for onClientNamesChange callback
        const buildClientNamesMap = (clientIds: (string | number)[]) => {
          const namesMap: Record<string, string> = {};
          clientIds.forEach((id) => {
            const strId = String(id);
            // Try to find client name from multiple sources
            const client = clientsForDisplay.find((c) => c.account_uid === strId) ||
                          allClientsForSelectAll.find((c) => c.account_uid === strId);
            if (client?.account_name) {
              namesMap[strId] = client.account_name;
            } else if (accountNames[strId]) {
              namesMap[strId] = accountNames[strId];
            }
          });
          return namesMap;
        };
        
        if (isSelected) {
          // Remove from selection - ensure no duplicates
          const newSelection = currentVals.filter((val) => val !== accountId);
          
          // Notify parent of updated client names BEFORE onChange
          if (onClientNamesChange) {
            const namesMap = buildClientNamesMap(newSelection);
            onClientNamesChange(namesMap);
          }
          
          onChange(newSelection);
        } else {
          // Add to selection - ensure no duplicates
          if (!currentVals.includes(accountId)) {
            const newSelection = [...currentVals, accountId];
            
            // Notify parent of updated client names BEFORE onChange
            if (onClientNamesChange) {
              const namesMap = buildClientNamesMap(newSelection);
              onClientNamesChange(namesMap);
            }
            
            onChange(newSelection as CheckboxValueType[]);
          }
        }
      }
    }
  };

  // Handle infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e;
      if (target instanceof HTMLElement) {
        const { scrollTop, scrollHeight, clientHeight } = target;
        const threshold = 50; // Load more when 50px from bottom

        if (
          scrollTop + clientHeight >= scrollHeight - threshold &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  // Alternative: Handle scroll via intersection observer for more reliable detection
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const dropdownContent = (
    <div
      className="bg-white shadow-lg rounded-lg flex flex-col text-black min-w-[320px] max-h-[650px] overflow-hidden"
      onScroll={handleScroll}
    >
             {/* Search input */}
      <div className="p-3 border-b border-gray-200">
         <Input
           placeholder="Search clients..."
           value={localSearchQuery}
           onChange={(e) => setLocalSearchQuery(e.target.value)}
          prefix={<SearchOutlined className="text-gray-400" />}
           allowClear
          className="w-full"
         />
       </div>

      {isLoading && (
        <div className="flex justify-center p-6">
          <Spin />
        </div>
      )}

      {isError && (
        <div className="text-red-500 p-4 text-center">
          Error: {error?.message || "Failed to load clients"}
        </div>
      )}

      {!isLoading && !isError && (
        <div className="flex flex-col">
          {/* Select All Clients section */}
          {!singleSelect && (
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
              <Checkbox
                indeterminate={isIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
                checked={isAllVisibleSelected}
                disabled={isLoadingAllClients}
                  className="text-sm font-medium"
              >
                  {isLoadingAllClients ? "Loading..." : "Select All Clients"}
              </Checkbox>
                <span className="text-sm text-gray-600 font-medium">
                {currentValues.length} / {availableClientsCount}
              </span>
              </div>
            </div>
          )}

          {/* Selected Clients section */}
          {selectedOptions.length > 0 && (
            <div key={`selected-${currentValues.length}`} className="p-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-800">Selected Clients</span>
                <span className="text-sm text-gray-600 font-medium">{selectedOptions.length}</span>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {selectedOptions.map(
                  (client) =>
                    client.account_uid && (
                      <div
                        key={`selected-${client.account_uid}`}
                        className="flex items-center p-2 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Checkbox
                          checked={true}
                          onChange={() => handleSingleSelect(client.account_uid!)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {client.account_name}
                        </span>
                      </div>
                    )
                )}
              </div>
            </div>
          )}

          {/* Active/Inactive Clients filters */}
          <div className="p-3 border-b border-gray-200 space-y-2">
            <div className="flex items-center justify-between">
              <Checkbox
                checked={activeClientsFilter}
                onChange={async (e) => {
                  const isChecked = e.target.checked;
                  setActiveClientsFilter(isChecked);
                  
                  if (isChecked) {
                    // Select all Active clients
                    const activeClients = deduplicatedAllClientsForSelectAll.filter(client =>
                      client.account_status === 'Active'
                    );
                    const activeClientIds = activeClients
                      .map((client) => client.account_uid)
                      .filter(Boolean) as string[];

                    if (useZustandMulti) {
                      if (contextId === 'userForm') {
                        // Add to existing selection for userForm context
                        const newSelection = [...new Set([...localSelectedClientIds, ...activeClientIds])];
                        setLocalSelectedClientIds(newSelection);
                        
                        // Update client names
                        const clientNamesMap = activeClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        const updatedNames = { ...localSelectedClientNames, ...clientNamesMap };
                        setLocalSelectedClientNames(updatedNames);
                        
                        if (onChange) {
                          onChange(newSelection as CheckboxValueType[]);
                        }
                        if (onClientNamesChange) {
                          onClientNamesChange(updatedNames);
                        }
                      } else {
                        // Add to existing selection for dashboard context
                        const newSelection = [...new Set([...selectedClientIds, ...activeClientIds])];
                        setSelectedClientIds(newSelection);
                        
                        // Update client names
                        const clientNamesMap = activeClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        setSelectedClientNames({ ...selectedClientNames, ...clientNamesMap });
                      }
                    } else if (onChange) {
                      // For non-Zustand mode, add to existing selection
                      const newSelection = [...new Set([...currentValues, ...activeClientIds])];
                      onChange(newSelection as CheckboxValueType[]);
                      
                      // Also send client names
                      if (onClientNamesChange) {
                        const clientNamesMap = activeClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        // Build complete names map for all selected clients
                        const completeNamesMap: Record<string, string> = {};
                        newSelection.forEach((id) => {
                          const strId = String(id);
                          completeNamesMap[strId] = clientNamesMap[strId] || accountNames[strId] || `Client ${strId}`;
                        });
                        onClientNamesChange(completeNamesMap);
                      }
                    }
                  } else {
                    // Deselect all Active clients
                    const activeClientIds = deduplicatedAllClientsForSelectAll
                      .filter(client => client.account_status === 'Active')
                      .map(client => client.account_uid)
                      .filter(Boolean) as string[];

                    if (useZustandMulti) {
                      if (contextId === 'userForm') {
                        // Remove from selection for userForm context
                        const newSelection = localSelectedClientIds.filter(id => !activeClientIds.includes(id));
                        setLocalSelectedClientIds(newSelection);
                        
                        // Remove client names
                        const updatedNames = { ...localSelectedClientNames };
                        activeClientIds.forEach(id => delete updatedNames[id]);
                        setLocalSelectedClientNames(updatedNames);
                        
                        if (onChange) {
                          onChange(newSelection as CheckboxValueType[]);
                        }
                        if (onClientNamesChange) {
                          onClientNamesChange(updatedNames);
                        }
                      } else {
                        // Remove from selection for dashboard context
                        const newSelection = selectedClientIds.filter(id => !activeClientIds.includes(id));
                        setSelectedClientIds(newSelection);
                        
                        // Remove client names
                        const updatedNames = { ...selectedClientNames };
                        activeClientIds.forEach(id => delete updatedNames[id]);
                        setSelectedClientNames(updatedNames);
                      }
                    } else if (onChange) {
                      // For non-Zustand mode, remove from existing selection
                      const newSelection = currentValues.filter(id => !activeClientIds.includes(id as string));
                      onChange(newSelection as CheckboxValueType[]);
                      
                      // Also update client names (remove deleted ones)
                      if (onClientNamesChange) {
                        const completeNamesMap: Record<string, string> = {};
                        newSelection.forEach((id) => {
                          const strId = String(id);
                          completeNamesMap[strId] = accountNames[strId] || `Client ${strId}`;
                        });
                        onClientNamesChange(completeNamesMap);
                      }
                    }
                  }
                }}
                className="text-sm font-medium"
              >
                Active Clients
              </Checkbox>
              <span className="text-sm text-gray-600 font-medium">
                {(() => {
                  const activeClients = deduplicatedAllClientsForSelectAll.filter(c => c.account_status === 'Active');
                  const selectedActiveCount = activeClients.filter(c => 
                    c.account_uid && currentValues.includes(c.account_uid)
                  ).length;
                  return `${selectedActiveCount} / ${activeClients.length}`;
                })()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <Checkbox
                checked={inactiveClientsFilter}
                onChange={async (e) => {
                  const isChecked = e.target.checked;
                  setInactiveClientsFilter(isChecked);
                  
                  if (isChecked) {
                    // Select all Inactive clients
                    const inactiveClients = deduplicatedAllClientsForSelectAll.filter(client =>
                      client.account_status === 'Inactive'
                    );
                    const inactiveClientIds = inactiveClients
                      .map((client) => client.account_uid)
                      .filter(Boolean) as string[];

                    if (useZustandMulti) {
                      if (contextId === 'userForm') {
                        // Add to existing selection for userForm context
                        const newSelection = [...new Set([...localSelectedClientIds, ...inactiveClientIds])];
                        setLocalSelectedClientIds(newSelection);
                        
                        // Update client names
                        const clientNamesMap = inactiveClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        const updatedNames = { ...localSelectedClientNames, ...clientNamesMap };
                        setLocalSelectedClientNames(updatedNames);
                        
                        if (onChange) {
                          onChange(newSelection as CheckboxValueType[]);
                        }
                        if (onClientNamesChange) {
                          onClientNamesChange(updatedNames);
                        }
                      } else {
                        // Add to existing selection for dashboard context
                        const newSelection = [...new Set([...selectedClientIds, ...inactiveClientIds])];
                        setSelectedClientIds(newSelection);
                        
                        // Update client names
                        const clientNamesMap = inactiveClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        setSelectedClientNames({ ...selectedClientNames, ...clientNamesMap });
                      }
                    } else if (onChange) {
                      // For non-Zustand mode, add to existing selection
                      const newSelection = [...new Set([...currentValues, ...inactiveClientIds])];
                      onChange(newSelection as CheckboxValueType[]);
                      
                      // Also send client names
                      if (onClientNamesChange) {
                        const clientNamesMap = inactiveClients.reduce((acc, client) => {
                          if (client.account_uid && client.account_name) {
                            acc[client.account_uid] = client.account_name;
                          }
                          return acc;
                        }, {} as Record<string, string>);
                        
                        // Build complete names map for all selected clients
                        const completeNamesMap: Record<string, string> = {};
                        newSelection.forEach((id) => {
                          const strId = String(id);
                          completeNamesMap[strId] = clientNamesMap[strId] || accountNames[strId] || `Client ${strId}`;
                        });
                        onClientNamesChange(completeNamesMap);
                      }
                    }
                  } else {
                    // Deselect all Inactive clients
                    const inactiveClientIds = deduplicatedAllClientsForSelectAll
                      .filter(client => client.account_status === 'Inactive')
                      .map(client => client.account_uid)
                      .filter(Boolean) as string[];

                    if (useZustandMulti) {
                      if (contextId === 'userForm') {
                        // Remove from selection for userForm context
                        const newSelection = localSelectedClientIds.filter(id => !inactiveClientIds.includes(id));
                        setLocalSelectedClientIds(newSelection);
                        
                        // Remove client names
                        const updatedNames = { ...localSelectedClientNames };
                        inactiveClientIds.forEach(id => delete updatedNames[id]);
                        setLocalSelectedClientNames(updatedNames);
                        
                        if (onChange) {
                          onChange(newSelection as CheckboxValueType[]);
                        }
                        if (onClientNamesChange) {
                          onClientNamesChange(updatedNames);
                        }
                      } else {
                        // Remove from selection for dashboard context
                        const newSelection = selectedClientIds.filter(id => !inactiveClientIds.includes(id));
                        setSelectedClientIds(newSelection);
                        
                        // Remove client names
                        const updatedNames = { ...selectedClientNames };
                        inactiveClientIds.forEach(id => delete updatedNames[id]);
                        setSelectedClientNames(updatedNames);
                      }
                    } else if (onChange) {
                      // For non-Zustand mode, remove from existing selection
                      const newSelection = currentValues.filter(id => !inactiveClientIds.includes(id as string));
                      onChange(newSelection as CheckboxValueType[]);
                      
                      // Also update client names (remove deleted ones)
                      if (onClientNamesChange) {
                        const completeNamesMap: Record<string, string> = {};
                        newSelection.forEach((id) => {
                          const strId = String(id);
                          completeNamesMap[strId] = accountNames[strId] || `Client ${strId}`;
                        });
                        onClientNamesChange(completeNamesMap);
                      }
                    }
                  }
                }}
                className="text-sm font-medium"
              >
                Inactive Clients
              </Checkbox>
              <span className="text-sm text-gray-600 font-medium">
                {(() => {
                  const inactiveClients = deduplicatedAllClientsForSelectAll.filter(c => c.account_status === 'Inactive');
                  const selectedInactiveCount = inactiveClients.filter(c => 
                    c.account_uid && currentValues.includes(c.account_uid)
                  ).length;
                  return `${selectedInactiveCount} / ${inactiveClients.length}`;
                })()}
              </span>
            </div>
          </div>

          {/* Unselected options */}
          {unselectedOptions.length > 0 && (
            <div key={`unselected-${currentValues.length}`} className="flex-1 overflow-y-auto max-h-48">
              <div className="p-3">
                <div className="space-y-1">
                  {unselectedOptions.map(
                    (client) =>
                      client.account_uid && (
                        <div
                          key={`unselected-${client.account_uid}`}
                          className="flex items-center p-2 hover:bg-gray-50 rounded transition-colors"
                        >
                          <Checkbox
                            checked={false}
                            onChange={() => handleSingleSelect(client.account_uid!)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 flex-1 truncate">
                            {client.account_name}
                          </span>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Load More Button / Indicator - Admin only */}
          {hasNextPage && isAdmin && (
            <div className="p-3 border-t border-gray-200 text-center">
              <Button 
                onClick={handleLoadMore} 
                disabled={isFetchingNextPage}
                size="small"
                className="w-full"
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const getDisplayText = () => {
    if (singleSelect) {
      if (!selectedClientId) return placeholder;
      const selectedAccount = clientsForDisplay.find(
        (acc) => acc.account_uid === selectedClientId
      );
      return (
        selectedAccount?.account_name ||
        selectedClientNames[selectedClientId] ||
        accountNames[selectedClientId] ||
        `Client ${selectedClientId}`
      );
    }

    if (currentValues.length === 0) return placeholder;
    if (currentValues.length === 1) {
      const singleId = currentValues[0];
      const selectedAccount = clientsForDisplay.find(
        (acc) => acc.account_uid === singleId
      );

      let name = placeholder;
      if (useZustandMulti) {
        if (contextId === 'userForm') {
          name = localSelectedClientNames[singleId as string] || accountNames[singleId] || `Client ${singleId}`;
        } else {
          name = selectedClientNames[singleId as string] || accountNames[singleId] || `Client ${singleId}`;
        }
      } else {
        name =
          selectedAccount?.account_name || accountNames[singleId] || `Client ${singleId}`;
      }
      return name;
    }
    return `${currentValues.length} clients selected`;
  };

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomLeft"
      disabled={isLoading && clientsForDisplay.length === 0}
      open={isDropdownOpen}
      onOpenChange={async (open) => {
        setIsDropdownOpen(open);
        clientDebugLog("dropdown_open_change", {
          contextId,
          open,
          isAdmin,
          totalClientsCount,
          isLoadingAllClients,
          allClientsCount: allClients.length,
          allClientsForSelectAllCount: allClientsForSelectAll.length,
        });
        if (!open) {
          setLocalSearchQuery('');
        } else {
          // When dropdown opens, fetch all clients if count is 0 and user is admin
          if (isAdmin && totalClientsCount === 0 && !isLoadingAllClients) {
            clientDebugLog("dropdown_open_trigger_fetch_all_clients", {
              contextId,
              reason: "admin_total_count_zero",
            });
            await fetchAllClients(true);
          }
        }
      }}
    >
      <Button
        className={
          customButtonClass ||
          `bg-[#2E5DDE] text-white border-none mr-2.5 pr-5 min-w-[200px] ${
            isLoading && clientsForDisplay.length === 0 ? "opacity-70" : ""
          }`
        }
      >
        {getDisplayText()} <DownOutlined className="ml-2" />
      </Button>
    </Dropdown>
  );
};

export default CheckboxDropdown;
